import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type AuthUser } from './api';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  suspended: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (u: AuthUser | null) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get<AuthUser>('/api/v1/auth/me');
        if (!cancelled) {
          setSuspended(false);
          setUser(me);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          if (!cancelled) setSuspended(true);
        } else if (!(err instanceof ApiError) || err.status !== 401) {
          // Surface unexpected errors to the console; don't crash the boot.
          console.error('Auth probe failed', err);
        }
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const me = await api.post<AuthUser>('/api/v1/auth/login', { email, password });
      setSuspended(false);
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setSuspended(true);
      throw err;
    }
  };

  const logout = async () => {
    await api.post('/api/v1/auth/logout');
    setSuspended(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, suspended, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
